import { authService } from '../../services/auth';
import {
  billingApi,
  type CancelBillingSubscriptionResultDto,
  type BillingOverviewDto,
  type PendingBillingAction,
  type UpdateBillingSubscriptionResultDto,
} from '../../services/billing.api';
import { resolveEuCountryCode } from '../../services/eu-countries';
import { type AccountMembersOverviewDto, type ProfileDto, profileApi } from '../../services/profile.api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { clearModalPortal, renderModalPortal } from '../shared/modal-portal';
import './rc-profile-view';
import { RcProfileView } from './rc-profile-view';
import { type BillingCancellationNoticeKind, type ProfileTab, type ProfileViewState } from './profile-shared';

type ProfileFormEventDetail = { form: HTMLFormElement };
type ProfilePlanEventDetail = { planKey: string; billingInterval: 'monthly' | 'yearly' };
type ProfileAccountTypeEventDetail = { isBusinessAccount: boolean };
type ProfileConsentEventDetail = { accepted: boolean };
type BillingQueryStatus = 'success' | 'processing' | null;

const PROFILE_EVENTS = [
  'tab-change',
  'save',
  'account-type-change',
  'open-billing-portal',
  'start-checkout',
  'update-subscription',
  'billing-terms-consent',
  'cancel-subscription',
  'switch-account-mode',
  'invite-member',
  'remove-member',
] as const;

export class RcProfile extends HTMLElement {
  private activeTab: ProfileTab = 'profile';
  private profile: ProfileDto | null = null;
  private billingOverview: Awaited<ReturnType<typeof billingApi.getOverview>> | null = null;
  private accountMembers: AccountMembersOverviewDto | null = null;
  private isLoading = false;
  private isSaving = false;
  private isPortalLoading = false;
  private checkoutLoadingKey = '';
  private subscriptionLoadingKey = '';
  private isCancellingSubscription = false;
  private error = '';
  private billingError = '';
  private billingTermsError = '';
  private billingTermsAccepted = false;
  private isBillingSyncing = false;
  private isSwitchingAccountMode = false;
  private accountModeError = '';
  private isManagingAccountMembers = false;
  private accountMembersError = '';
  private cancellationNotice: BillingCancellationNoticeKind | null = null;
  private disposeLocaleChange?: () => void;
  private billingSyncRunId = 0;
  private handledHash = '';

  private get isBillingActionPending(): boolean {
    return this.isBillingSyncing
      || this.isPortalLoading
      || !!this.checkoutLoadingKey
      || !!this.subscriptionLoadingKey
      || this.isCancellingSubscription;
  }

  private boundAuthChange = (authenticated: boolean) => {
    if (!authenticated) {
      this.profile = null;
      this.billingOverview = null;
      this.isLoading = false;
      this.error = '';
      this.billingError = '';
      this.billingTermsError = '';
      this.billingTermsAccepted = false;
      this.isBillingSyncing = false;
      this.isSwitchingAccountMode = false;
      this.accountModeError = '';
      this.accountMembers = null;
      this.isManagingAccountMembers = false;
      this.accountMembersError = '';
      this.render();
      return;
    }

    void this.loadActiveTabData();
  };

  connectedCallback(): void {
    this.syncTabFromQueryString();
    PROFILE_EVENTS.forEach((eventName) => this.addEventListener(`profile-${eventName}`, this.handleProfileEvent as EventListener));
    this.render();
    renderModalPortal(this, '').addEventListener('click', this.handlePortalClick);
    authService.onAuthChange(this.boundAuthChange);
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    window.addEventListener('hashchange', this.handleHashChange);

    if (authService.isAuthenticated) {
      void this.loadActiveTabData();
    }
  }

  disconnectedCallback(): void {
    this.cancelPendingBillingSync();
    this.disposeLocaleChange?.();
    PROFILE_EVENTS.forEach((eventName) => this.removeEventListener(`profile-${eventName}`, this.handleProfileEvent as EventListener));
    window.removeEventListener('hashchange', this.handleHashChange);
    renderModalPortal(this, '').removeEventListener('click', this.handlePortalClick);
    clearModalPortal(this);
  }

  private syncTabFromQueryString(): void {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'subscription' || tab === 'invoices' || tab === 'profile') {
      this.activeTab = tab;
    }

    this.ensureActiveTabIsAllowed();
  }

  private async loadActiveTabData(): Promise<void> {
    this.ensureActiveTabIsAllowed();

    if (this.activeTab === 'profile') {
      await this.loadProfileData();
      return;
    }

    await this.loadBillingData();
  }

  private async loadProfileData(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.accountMembersError = '';
    this.render();

    try {
      await this.claimInviteFromQueryIfPresent();
      const [profileResult, accountMembersResult] = await Promise.allSettled([
        profileApi.get(),
        profileApi.getAccountMembers(),
      ]);

      if (profileResult.status === 'fulfilled') {
        this.profile = profileResult.value;
      } else {
        this.error = profileResult.reason?.message || t('profile.loadError');
      }

      if (accountMembersResult.status === 'fulfilled') {
        this.accountMembers = accountMembersResult.value;
      } else {
        this.accountMembers = null;
        this.accountMembersError = accountMembersResult.reason?.message || t('profile.accountMembers.loadError');
      }
    } catch (err: any) {
      this.error = err?.message || t('profile.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private async loadBillingData(): Promise<void> {
    this.isLoading = true;
    this.billingError = '';
    this.billingTermsError = '';
    this.billingTermsAccepted = false;
    this.isBillingSyncing = false;
    this.render();

    try {
      const [billingOverviewResult, profileResult] = await Promise.allSettled([
        billingApi.getOverview(),
        this.profile ? Promise.resolve(this.profile) : profileApi.get(),
      ]);

      if (billingOverviewResult.status === 'fulfilled') {
        this.billingOverview = billingOverviewResult.value;
      } else {
        throw billingOverviewResult.reason;
      }

      if (profileResult.status === 'fulfilled') {
        this.profile = profileResult.value;
      }

      void this.handleBillingReturnState(this.billingOverview);
    } catch (err: any) {
      const message = err?.message || t('profile.loadError');
      this.billingError = message;
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated) {
      this.innerHTML = `<div class="empty-state">${t('profile.notLoggedIn')}</div>`;
      return;
    }

    const user = authService.currentUser!;
    const providerLabel = user.provider === 'google'
      ? 'Google'
      : user.provider === 'microsoft'
        ? 'Microsoft'
        : user.provider === 'email'
          ? t('auth.emailProvider')
          : user.provider;

    this.innerHTML = '<rc-profile-view></rc-profile-view>';
    const view = this.querySelector('rc-profile-view') as RcProfileView | null;
    if (!view) return;
    view.state = this.getViewState(user.name, user.email, providerLabel);
    view.render();
    this.renderCancellationNotice();
    this.scheduleHashNavigation();
  }

  private handleHashChange = (): void => {
    this.handledHash = '';
    this.scheduleHashNavigation();
  };

  private getViewState(userName: string, userEmail: string, providerLabel: string): ProfileViewState {
    return {
      activeTab: this.activeTab,
      profile: this.profile,
      accountMembers: this.accountMembers,
      billingOverview: this.billingOverview,
      isBillingSyncing: this.isBillingSyncing,
      isLoading: this.isLoading,
      isSaving: this.isSaving,
      isPortalLoading: this.isPortalLoading,
      checkoutLoadingKey: this.checkoutLoadingKey,
      subscriptionLoadingKey: this.subscriptionLoadingKey,
      isCancellingSubscription: this.isCancellingSubscription,
      error: this.error,
      billingError: this.billingError,
      billingTermsError: this.billingTermsError,
      billingTermsAccepted: this.billingTermsAccepted,
      userName,
      userEmail,
      providerLabel,
      isAdmin: authService.isAdmin,
      canManageAccount: this.profile?.canManageAccount !== false,
      allowedTabs: this.getAllowedTabs(),
      restrictedAccess: this.hasRestrictedAccess(),
      isSwitchingAccountMode: this.isSwitchingAccountMode,
      accountModeError: this.accountModeError,
      isManagingAccountMembers: this.isManagingAccountMembers,
      accountMembersError: this.accountMembersError,
    };
  }

  private handleProfileEvent = async (event: Event): Promise<void> => {
    switch ((event as CustomEvent).type) {
      case 'profile-tab-change': {
        const tab = (event as CustomEvent<{ tab?: ProfileTab }>).detail?.tab;
        if (!tab) return;
        this.activeTab = tab;
        this.ensureActiveTabIsAllowed();
        this.syncQueryStringWithActiveTab();
        this.render();
        await this.loadActiveTabData();
        return;
      }
      case 'profile-save':
        await this.saveProfile((event as CustomEvent<ProfileFormEventDetail>).detail.form);
        return;
      case 'profile-account-type-change':
        this.syncAccountType((event as CustomEvent<ProfileAccountTypeEventDetail>).detail?.isBusinessAccount === true);
        return;
      case 'profile-open-billing-portal':
        await this.openBillingPortal();
        return;
      case 'profile-start-checkout': {
        const detail = (event as CustomEvent<ProfilePlanEventDetail>).detail;
        if (!this.ensureBillingTermsAccepted()) return;
        await this.startCheckout(detail.planKey, detail.billingInterval);
        return;
      }
      case 'profile-update-subscription': {
        const detail = (event as CustomEvent<ProfilePlanEventDetail>).detail;
        if (!this.ensureBillingTermsAccepted()) return;
        await this.updateSubscription(detail.planKey, detail.billingInterval);
        return;
      }
      case 'profile-billing-terms-consent':
        this.syncBillingTermsConsent((event as CustomEvent<ProfileConsentEventDetail>).detail?.accepted === true);
        return;
      case 'profile-cancel-subscription':
        await this.cancelSubscription();
        return;
      case 'profile-switch-account-mode':
        await this.switchAccountMode((event as CustomEvent<{ accountMode?: 'landlord' | 'tenant' }>).detail?.accountMode);
        return;
      case 'profile-invite-member':
        await this.inviteAccountMember((event as CustomEvent<{ form?: HTMLFormElement }>).detail?.form);
        return;
      case 'profile-remove-member':
        await this.removeAccountMember((event as CustomEvent<{ memberUserId?: string }>).detail?.memberUserId);
        return;
      default:
        return;
    }
  };

  private syncAccountType(isBusinessAccount: boolean): void {
    const user = authService.currentUser;
    if (!user) return;
    const current = this.getProfileModel(user.name, user.email);
    this.profile = {
      ...current,
      isBusinessAccount,
      companyName: isBusinessAccount ? current.companyName : null,
      vatNumber: isBusinessAccount ? current.vatNumber : null,
      billingStreet: isBusinessAccount ? current.billingStreet : null,
      billingHouseNumber: isBusinessAccount ? current.billingHouseNumber : null,
      billingPostalCode: isBusinessAccount ? current.billingPostalCode : null,
      billingCity: isBusinessAccount ? current.billingCity : null,
      billingCountry: isBusinessAccount ? current.billingCountry : null,
    };
  }

  private async saveProfile(form: HTMLFormElement): Promise<void> {
    if (this.isSaving) return;

    const fd = new FormData(form);
    const isBusinessAccount = fd.get('accountType') === 'business';
    const draftProfile = this.toDraftProfile(fd, isBusinessAccount);

    this.isSaving = true;
    this.error = '';
    this.profile = draftProfile;
    this.render();

    try {
      this.profile = await profileApi.update({
        customDisplayName: null,
        firstName: draftProfile.firstName ?? null,
        lastName: draftProfile.lastName ?? null,
        isBusinessAccount: draftProfile.isBusinessAccount,
        companyName: draftProfile.companyName ?? null,
        vatNumber: draftProfile.vatNumber ?? null,
        street: draftProfile.street ?? null,
        houseNumber: draftProfile.houseNumber ?? null,
        postalCode: draftProfile.postalCode ?? null,
        city: draftProfile.city ?? null,
        country: draftProfile.country ?? null,
        billingStreet: draftProfile.billingStreet ?? null,
        billingHouseNumber: draftProfile.billingHouseNumber ?? null,
        billingPostalCode: draftProfile.billingPostalCode ?? null,
        billingCity: draftProfile.billingCity ?? null,
        billingCountry: draftProfile.billingCountry ?? null,
      });
      authService.updateCurrentUserName(this.profile.displayName);
      this.accountMembers = await profileApi.getAccountMembers().catch(() => this.accountMembers);
      this.billingOverview = await billingApi.getOverview();
      toastService.success(t('profile.saved'));
    } catch (err: any) {
      this.error = err?.message || t('profile.saveError');
    } finally {
      this.isSaving = false;
      this.render();
    }
  }

  private async openBillingPortal(): Promise<void> {
    if (this.isBillingActionPending) return;

    this.isPortalLoading = true;
    this.billingError = '';
    this.render();

    try {
      const session = await billingApi.createPortalSession();
      window.location.href = session.url;
    } catch (err: any) {
      this.billingError = err?.message || t('billing.portalError');
      toastService.error(this.billingError);
    } finally {
      this.isPortalLoading = false;
      this.render();
    }
  }

  private async startCheckout(planKey: string, billingInterval: 'monthly' | 'yearly'): Promise<void> {
    const loadingKey = `${planKey}:${billingInterval}`;
    if (this.isBillingActionPending) {
      return;
    }

    this.checkoutLoadingKey = loadingKey;
    this.billingError = '';
    this.render();

    try {
      const session = await billingApi.createCheckoutSession({ planKey, billingInterval });
      billingApi.storePendingBillingAction({ kind: 'checkout', planKey, billingInterval });
      window.location.href = session.url;
    } catch (err: any) {
      billingApi.clearPendingBillingAction();
      this.billingError = err?.message || t('billing.checkoutError');
      toastService.error(this.billingError);
    } finally {
      this.checkoutLoadingKey = '';
      this.render();
    }
  }

  private async updateSubscription(planKey: string, billingInterval: 'monthly' | 'yearly'): Promise<void> {
    const loadingKey = `${planKey}:${billingInterval}`;
    if (this.isBillingActionPending) {
      return;
    }

    this.subscriptionLoadingKey = loadingKey;
    this.billingError = '';
    this.render();

    try {
      const result = await billingApi.updateSubscription({ planKey, billingInterval });
      await this.handleSubscriptionUpdateResult(result);
    } catch (err: any) {
      this.billingError = err?.message || t('billing.subscriptionUpdateError');
      toastService.error(this.billingError);
    } finally {
      this.subscriptionLoadingKey = '';
      this.render();
    }
  }

  private async cancelSubscription(): Promise<void> {
    if (this.isBillingActionPending) {
      return;
    }

    if (!await confirmDialog(t('billing.cancelConfirm'), t('billing.cancelSubscription'))) {
      return;
    }

    this.isCancellingSubscription = true;
    this.billingError = '';
    this.render();

    try {
      const result = await billingApi.cancelSubscription();
      this.billingOverview = await billingApi.getOverview();
      this.handleCancellationResult(result);
    } catch (err: any) {
      this.billingError = err?.message || t('billing.subscriptionCancelError');
      toastService.error(this.billingError);
    } finally {
      this.isCancellingSubscription = false;
      this.render();
    }
  }

  private async handleSubscriptionUpdateResult(result: UpdateBillingSubscriptionResultDto): Promise<void> {
    if (result.status === 'requires_payment' && result.url) {
      this.storePendingSubscriptionChange();
      window.location.href = result.url;
      return;
    }

    this.billingOverview = await billingApi.getOverview();
    toastService.success(
      result.status === 'scheduled'
        ? t('billing.subscriptionUpdatedScheduled')
        : t('billing.subscriptionUpdatedImmediate'),
    );
  }

  private storePendingSubscriptionChange(): void {
    const subscription = this.billingOverview?.subscription;
    const loadingKey = this.subscriptionLoadingKey;
    const separatorIndex = loadingKey.indexOf(':');
    if (!subscription || separatorIndex <= 0) {
      return;
    }

    const planKey = loadingKey.slice(0, separatorIndex);
    const billingInterval = loadingKey.slice(separatorIndex + 1);
    if (billingInterval !== 'monthly' && billingInterval !== 'yearly') {
      return;
    }

    billingApi.storePendingBillingAction({
      kind: 'subscription_change',
      planKey,
      billingInterval,
    });
  }

  private getBillingQueryStatus(): BillingQueryStatus {
    const status = new URLSearchParams(window.location.search).get('billing');
    return status === 'success' || status === 'processing' ? status : null;
  }

  private async handleBillingReturnState(overview: BillingOverviewDto): Promise<void> {
    const billingStatus = this.getBillingQueryStatus();
    if (!billingStatus) {
      this.isBillingSyncing = false;
      return;
    }

    if (billingStatus === 'success') {
      this.isBillingSyncing = false;
      this.clearBillingQueryStatus();
      billingApi.clearPendingBillingAction();
      toastService.success(t('billing.checkoutSuccess'));
      return;
    }

    const pendingAction = billingApi.getPendingBillingAction();
    if (!pendingAction) {
      this.isBillingSyncing = true;
      this.render();
      toastService.warning(t('billing.checkoutProcessing'), 15000);
      return;
    }

    if (this.isPendingBillingActionResolved(pendingAction, overview)) {
      this.finishPendingBillingAction(pendingAction);
      return;
    }

    this.isBillingSyncing = true;
    this.render();
    toastService.warning(t('billing.checkoutProcessing'), 15000);
    this.startPendingBillingSync(pendingAction);
  }

  private startPendingBillingSync(action: PendingBillingAction): void {
    const runId = ++this.billingSyncRunId;
    void this.pollPendingBillingAction(runId, action, 20, 3000);
  }

  private cancelPendingBillingSync(): void {
    this.isBillingSyncing = false;
    this.billingSyncRunId += 1;
  }

  private async pollPendingBillingAction(
    runId: number,
    action: PendingBillingAction,
    remainingAttempts: number,
    intervalMs: number,
  ): Promise<void> {
    if (remainingAttempts <= 0) {
      return;
    }

    await this.delay(intervalMs);
    if (runId !== this.billingSyncRunId) {
      return;
    }

    try {
      const overview = await billingApi.getOverview();
      if (runId !== this.billingSyncRunId) {
        return;
      }

      this.billingOverview = overview;
      this.isBillingSyncing = true;
      this.render();

      if (this.isPendingBillingActionResolved(action, overview)) {
        this.finishPendingBillingAction(action);
        return;
      }
    } catch {
      // Keep retrying while the webhook or API state catches up.
    }

    await this.pollPendingBillingAction(runId, action, remainingAttempts - 1, intervalMs);
  }

  private isPendingBillingActionResolved(action: PendingBillingAction, overview: BillingOverviewDto): boolean {
    const subscription = overview.subscription;
    const matchesTargetPlan =
      subscription.planKey?.toLowerCase() === action.planKey.toLowerCase() &&
      this.normalizeBillingInterval(subscription.interval) === action.billingInterval;

    if (!matchesTargetPlan) {
      return false;
    }

    if (action.kind === 'checkout') {
      return subscription.hasManageableSubscription ||
        (!!subscription.providerSubscriptionId && subscription.status !== 'trialing');
    }

    return !subscription.pendingPlanKey;
  }

  private finishPendingBillingAction(action: PendingBillingAction): void {
    this.cancelPendingBillingSync();
    this.isBillingSyncing = false;
    this.clearBillingQueryStatus();
    billingApi.clearPendingBillingAction();
    this.render();
    toastService.success(
      action.kind === 'checkout'
        ? t('billing.checkoutSuccess')
        : t('billing.subscriptionUpdated'),
    );
  }

  private clearBillingQueryStatus(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('billing')) {
      return;
    }

    url.searchParams.delete('billing');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private normalizeBillingInterval(value?: string | null): 'monthly' | 'yearly' | null {
    if (value === 'yearly') {
      return 'yearly';
    }

    if (value === 'monthly') {
      return 'monthly';
    }

    return null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private getCurrentAccountMode(): 'landlord' | 'tenant' {
    if (this.profile?.accountMode === 'tenant') {
      return 'tenant';
    }

    return authService.currentUser?.accountMode === 'tenant' ? 'tenant' : 'landlord';
  }

  private getAllowedTabs(): ProfileTab[] {
    return this.getCurrentAccountMode() === 'tenant'
      ? ['profile']
      : ['profile', 'subscription', 'invoices'];
  }

  private ensureActiveTabIsAllowed(): void {
    if (this.getAllowedTabs().includes(this.activeTab)) {
      return;
    }

    this.activeTab = 'profile';
    this.syncQueryStringWithActiveTab();
  }

  private syncQueryStringWithActiveTab(): void {
    const url = new URL(window.location.href);
    if (this.activeTab === 'profile') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', this.activeTab);
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState({}, '', nextUrl);
    }
  }

  private scheduleHashNavigation(): void {
    if (this.activeTab !== 'profile') {
      return;
    }

    const hash = window.location.hash;
    if (!hash || this.handledHash === hash) {
      return;
    }

    window.requestAnimationFrame(() => {
      const target = this.querySelector<HTMLElement>(hash);
      if (!target) {
        return;
      }

      this.handledHash = hash;
      target.scrollIntoView({ block: 'start' });
    });
  }

  private toDraftProfile(fd: FormData, isBusinessAccount: boolean): ProfileDto {
    const user = authService.currentUser;
    const fallbackName = user?.name || '';
    const fallbackEmail = user?.email || '';
    const current = this.getProfileModel(fallbackName, fallbackEmail);

    const allowedCountryCodes = current.allowedCountryCodes || [];

    return {
      ...current,
      customDisplayName: null,
      firstName: this.normalizeFormValue(fd.get('firstName')),
      lastName: this.normalizeFormValue(fd.get('lastName')),
      displayName: this.composeDisplayName(
        this.normalizeFormValue(fd.get('firstName')),
        this.normalizeFormValue(fd.get('lastName')),
        current,
      ),
      isBusinessAccount,
      companyName: isBusinessAccount ? this.normalizeFormValue(fd.get('companyName')) : null,
      vatNumber: isBusinessAccount ? this.normalizeFormValue(fd.get('vatNumber')) : null,
      street: this.normalizeFormValue(fd.get('street')),
      houseNumber: this.normalizeFormValue(fd.get('houseNumber')),
      postalCode: this.normalizeFormValue(fd.get('postalCode')),
      city: this.normalizeFormValue(fd.get('city')),
      country: resolveEuCountryCode(typeof fd.get('country') === 'string' ? fd.get('country') as string : '', allowedCountryCodes),
      billingStreet: isBusinessAccount ? this.normalizeFormValue(fd.get('billingStreet')) : null,
      billingHouseNumber: isBusinessAccount ? this.normalizeFormValue(fd.get('billingHouseNumber')) : null,
      billingPostalCode: isBusinessAccount ? this.normalizeFormValue(fd.get('billingPostalCode')) : null,
      billingCity: isBusinessAccount ? this.normalizeFormValue(fd.get('billingCity')) : null,
      billingCountry: isBusinessAccount ? resolveEuCountryCode(typeof fd.get('billingCountry') === 'string' ? fd.get('billingCountry') as string : '', allowedCountryCodes) : null,
    };
  }

  private getProfileModel(name: string, email: string): ProfileDto {
    return this.profile ?? {
      email,
      displayName: name,
      providerDisplayName: name,
      customDisplayName: null,
      accountMode: authService.currentUser?.accountMode || 'landlord',
      allowedCountryCodes: [],
      firstName: '',
      lastName: '',
      isBusinessAccount: false,
      companyName: '',
      vatNumber: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      billingStreet: '',
      billingHouseNumber: '',
      billingPostalCode: '',
      billingCity: '',
      billingCountry: '',
    };
  }

  private normalizeFormValue(value: FormDataEntryValue | null): string | null {
    const text = (typeof value === 'string' ? value : '').trim();
    return text || null;
  }

  private composeDisplayName(firstName: string | null, lastName: string | null, current: ProfileDto): string {
    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return fullName || current.displayName || current.providerDisplayName || '';
  }

  private syncBillingTermsConsent(isAccepted: boolean): void {
    this.billingTermsAccepted = isAccepted;

    if (isAccepted && this.billingTermsError) {
      this.billingTermsError = '';
      this.render();
    }
  }

  private ensureBillingTermsAccepted(): boolean {
    if (this.billingTermsAccepted) {
      if (this.billingTermsError) {
        this.billingTermsError = '';
        this.render();
      }

      return true;
    }

    this.billingTermsError = t('billing.changeTerms.validationError');
    this.render();

    const nextCheckbox = this.querySelector<HTMLInputElement>('[data-billing-terms-consent]');
    nextCheckbox?.focus();
    nextCheckbox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }

  private hasRestrictedAccess(): boolean {
    return this.billingOverview?.hasApplicationAccess === false;
  }

  private handleCancellationResult(result: CancelBillingSubscriptionResultDto): void {
    if (result.status === 'cooling_off_trial_started') {
      this.cancellationNotice = 'trial_started';
      return;
    }

    if (result.status === 'cooling_off_profile_only') {
      this.cancellationNotice = 'profile_only';
      return;
    }

    toastService.success(t('billing.subscriptionCancelled'));
  }

  private renderCancellationNotice(): void {
    if (!this.cancellationNotice) {
      renderModalPortal(this, '');
      return;
    }

    const title = this.cancellationNotice === 'trial_started'
      ? t('billing.coolingOffTrialStartedTitle')
      : t('billing.coolingOffProfileOnlyTitle');
    const body = this.cancellationNotice === 'trial_started'
      ? t('billing.coolingOffTrialStartedBody')
      : t('billing.coolingOffProfileOnlyBody');

    renderModalPortal(this, `
      <div class="modal-overlay billing-cancel-notice-modal" role="dialog" aria-modal="true" aria-labelledby="billing-cancel-notice-title">
        <div class="modal billing-cancel-notice">
          <button type="button" class="modal-close billing-cancel-notice-close" aria-label="${this.escapeHtml(t('common.close'))}">
            &times;
          </button>
          <div class="billing-cancel-notice-copy">
            <strong id="billing-cancel-notice-title">${this.escapeHtml(title)}</strong>
            <p>${this.escapeHtml(body)}</p>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-primary" data-action="dismiss-billing-cancel-notice">${this.escapeHtml(t('common.close'))}</button>
          </div>
        </div>
      </div>
    `);
  }

  private handlePortalClick = (event: Event): void => {
    const rawTarget = event.target as HTMLElement | null;
    const closeTarget = rawTarget?.closest<HTMLElement>('.billing-cancel-notice-close, [data-action="dismiss-billing-cancel-notice"]');
    if (closeTarget || rawTarget?.classList.contains('billing-cancel-notice-modal')) {
      this.cancellationNotice = null;
      this.renderCancellationNotice();
    }
  };

  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  private async switchAccountMode(nextMode?: 'landlord' | 'tenant'): Promise<void> {
    if (!nextMode || this.isSwitchingAccountMode) {
      return;
    }

    if (!await this.confirmAccountModeSwitch(nextMode)) {
      return;
    }

    this.isSwitchingAccountMode = true;
    this.accountModeError = '';
    this.render();

    try {
      const result = await profileApi.switchAccountMode({ accountMode: nextMode });
      authService.updateCurrentUser({ accountMode: result.accountMode });
      const [profile, accountMembers] = await Promise.all([
        profileApi.get(),
        profileApi.getAccountMembers().catch(() => this.accountMembers),
      ]);
      this.profile = profile;
      this.accountMembers = accountMembers;
      this.billingOverview = await billingApi.getOverview().catch(() => this.billingOverview);
      toastService.success(
        result.accountMode === 'tenant'
          ? t('profile.accountModeTenantActivated')
          : result.trialStarted
            ? t('profile.accountModeLandlordTrialActivated')
            : t('profile.accountModeLandlordActivated'),
      );
      this.activeTab = result.accountMode === 'landlord' ? 'subscription' : 'profile';
      this.syncQueryStringWithActiveTab();
      if (result.accountMode === 'tenant') {
        window.location.href = '/tenant/active/';
        return;
      }
    } catch (err: any) {
      this.accountModeError = err?.message || t('profile.accountModeSwitchError');
      if (nextMode === 'landlord') {
        this.activeTab = 'subscription';
        this.billingError = this.accountModeError;
        this.syncQueryStringWithActiveTab();
      }
    } finally {
      this.isSwitchingAccountMode = false;
      this.render();
    }
  }

  private confirmAccountModeSwitch(nextMode: 'landlord' | 'tenant'): Promise<boolean> {
    if (nextMode === 'landlord') {
      return confirmDialog(
        t('profile.accountModeSwitchToLandlordConfirm'),
        t('profile.switchToLandlord'),
        {
          title: t('profile.accountModeSwitchToLandlordTitle'),
          confirmVariant: 'primary',
        },
      );
    }

    return confirmDialog(
      t('profile.accountModeSwitchToTenantConfirm'),
      t('profile.switchToTenant'),
      {
        title: t('profile.accountModeSwitchToTenantTitle'),
        confirmVariant: 'primary',
      },
    );
  }

  private async inviteAccountMember(form?: HTMLFormElement): Promise<void> {
    if (!form || this.isManagingAccountMembers) {
      return;
    }

    const formData = new FormData(form);
    const email = this.normalizeFormValue(formData.get('inviteEmail'));
    const accessRole = formData.get('accessRole');
    if (!email || (accessRole !== 'manager' && accessRole !== 'reader')) {
      this.accountMembersError = t('profile.accountMembers.inviteError');
      this.render();
      return;
    }

    this.isManagingAccountMembers = true;
    this.accountMembersError = '';
    this.render();

    try {
      await profileApi.inviteAccountMember({ email, accessRole });
      this.accountMembers = await profileApi.getAccountMembers();
      toastService.success(t('profile.accountMembers.inviteSent'));
      form.reset();
    } catch (err: any) {
      this.accountMembersError = err?.message || t('profile.accountMembers.inviteError');
    } finally {
      this.isManagingAccountMembers = false;
      this.render();
    }
  }

  private async removeAccountMember(memberUserId?: string): Promise<void> {
    if (!memberUserId || this.isManagingAccountMembers) {
      return;
    }

    if (!await confirmDialog(t('profile.accountMembers.removeConfirm'), t('profile.accountMembers.removeButton'))) {
      return;
    }

    this.isManagingAccountMembers = true;
    this.accountMembersError = '';
    this.render();

    try {
      await profileApi.removeAccountMember(memberUserId);
      this.accountMembers = await profileApi.getAccountMembers();
      toastService.success(t('profile.accountMembers.removeSuccess'));
    } catch (err: any) {
      this.accountMembersError = err?.message || t('profile.accountMembers.removeError');
    } finally {
      this.isManagingAccountMembers = false;
      this.render();
    }
  }

  private getInviteTokenFromQuery(): string | null {
    const token = new URLSearchParams(window.location.search).get('accountInvite');
    return token?.trim() || null;
  }

  private clearInviteTokenFromQuery(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('accountInvite');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private async claimInviteFromQueryIfPresent(): Promise<void> {
    const token = this.getInviteTokenFromQuery();
    if (!token) {
      return;
    }

    try {
      await profileApi.claimAccountMemberInvite({ token });
      this.clearInviteTokenFromQuery();
      toastService.success(t('profile.accountMembers.claimSuccess'));
    } catch (err: any) {
      this.accountMembersError = err?.message || t('profile.accountMembers.claimError');
      this.clearInviteTokenFromQuery();
    }
  }

}

if (!customElements.get('rc-profile')) {
  customElements.define('rc-profile', RcProfile);
}
